import type { ComponentType } from 'react';

/**
 * The attribute names `r-mount` reads.
 *
 * Two, and that is the whole of it: which host component to render, and the
 * props the author filled in for it. The host application supplies the actual
 * React component through `RuleCMSWidget`'s `mounts` map. This library never
 * imports customer code.
 *
 * There is no hide attribute. Hide lives on the column, like every other
 * component. There is no children slot — authoring a subtree into a card is
 * unshipped platform work, and a mount is a leaf that *is* the component.
 */

export const MOUNT_NAME_ATTRIBUTE = 'mount-name-3-resolutions';
export const MOUNT_PROPS_ATTRIBUTE = 'mount-props-3-resolutions';

export const MOUNT_PROP_TYPES = ['string', 'number', 'boolean', 'json'] as const;
export type MountPropType = (typeof MOUNT_PROP_TYPES)[number];

export type MountPropRow = {
  key: string;
  type: MountPropType;
  value: string;
};

export type MountsMap = {
  [name: string]: ComponentType<any>;
};

/**
 * A name the host can register. Starts with a letter so it cannot collide
 * with a number-looking key or a CSS custom property, then letters, digits,
 * hyphens and underscores only. The same grammar the composer field accepts.
 */
export const MOUNT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;

const FORBIDDEN_PROP_KEYS = new Set([
  '__proto__',
  'constructor',
  'prototype',
  'key',
  'ref',
  'children',
  'dangerouslySetInnerHTML',
]);

const HTML_EVENT_KEYS = new Set([
  'onclick',
  'onsubmit',
  'onload',
  'onerror',
  'onmouseover',
  'onmouseenter',
  'onfocus',
  'onblur',
  'onchange',
  'oninput',
  'onkeydown',
  'onkeyup',
]);

export const isMountPropType = (value: unknown): value is MountPropType =>
  typeof value === 'string' && (MOUNT_PROP_TYPES as readonly string[]).includes(value);

export const isAllowedMountName = (value: unknown): value is string =>
  typeof value === 'string' && MOUNT_NAME_PATTERN.test(value);

/**
 * Keys a marketer-authored row must never become. `on*` would turn a typed
 * string into an event handler; `dangerouslySetInnerHTML` would inject HTML;
 * the rest are reserved by React or by the prototype chain.
 */
export const isForbiddenMountPropKey = (key: string): boolean => {
  if (FORBIDDEN_PROP_KEYS.has(key) || HTML_EVENT_KEYS.has(key.toLowerCase())) {
    return true;
  }
  return /^on[A-Z]/.test(key);
};

export const resolveMountName = (
  componentAttributes?: { [key: string]: unknown }
): string => {
  const value = componentAttributes?.[MOUNT_NAME_ATTRIBUTE];
  return isAllowedMountName(value) ? value : '';
};

const isMountPropRow = (value: unknown): value is MountPropRow => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const row = value as { key?: unknown; type?: unknown; value?: unknown };
  return (
    typeof row.key === 'string' &&
    isMountPropType(row.type) &&
    typeof row.value === 'string'
  );
};

const coerceMountPropValue = (row: MountPropRow): unknown => {
  switch (row.type) {
    case 'string':
      return row.value;
    case 'number': {
      const parsed = Number(row.value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case 'boolean':
      if (row.value === 'true') {
        return true;
      }
      if (row.value === 'false') {
        return false;
      }
      return undefined;
    case 'json':
      try {
        return JSON.parse(row.value);
      } catch {
        return undefined;
      }
    default:
      return undefined;
  }
};

/**
 * Turns the authored rows into a props object for the host component.
 * Unknown shapes, forbidden keys, and values that fail their declared type
 * are dropped rather than guessed.
 */
export const resolveMountProps = (
  componentAttributes?: { [key: string]: unknown }
): { [key: string]: unknown } => {
  const raw = componentAttributes?.[MOUNT_PROPS_ATTRIBUTE];
  if (!Array.isArray(raw)) {
    return {};
  }

  const props: { [key: string]: unknown } = {};
  for (const entry of raw) {
    if (!isMountPropRow(entry)) {
      continue;
    }
    const key = entry.key.trim();
    if (!key || isForbiddenMountPropKey(key)) {
      continue;
    }
    const coerced = coerceMountPropValue(entry);
    if (coerced === undefined) {
      continue;
    }
    props[key] = coerced;
  }
  return props;
};
